// FOR REVIEW - review this whole file and all it's endpoints
// src/store.js
import {
  configureStore,
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { axiosInstance } from "services/axios-supabase";
import { supabase } from "services/supabase";
import _ from "lodash";

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  loading: false,
  error: null,
  connections: [],
  connectionsLoaded: false,
};

export const selectConnectionsLoaded = (state) =>
  state.connectionReducer.connectionsLoaded;
export const selectConnections = (state) =>
  state.connectionReducer.connections;
export const selectNumberOfConnections = (state) =>
  state.connectionReducer.connections?.length ?? 0;

// ------------------------------------------------------------------------

export const addCalendarLink = createAsyncThunk(
  "data/addCalendarLink",
  async ({ connection_id, calendar_id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/calendars`, {
        connection_id: connection_id,
        calendar_id: calendar_id,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeCalendarLink = createAsyncThunk(
  "data/removeCalendarLink",
  async ({ gcal_id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/calendars/${gcal_id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateCalendarClient = createAsyncThunk(
  "data/updateCalendarClient",
  async ({ gcal_id, client_id }, { rejectWithValue }) => {
    try {
      // Update the calendar's client_id
      const response = await axiosInstance.put(`/calendars/${gcal_id}`, {
        client_id: client_id,
      });

      // If client_id is provided, fetch the client data
      let clientData = null;
      if (client_id) {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();

          if (user) {
            const { data: client, error: clientError } = await supabase
              .from("clients")
              .select("*")
              .eq("id", client_id)
              .eq("uid", user.id)
              .single();

            if (!clientError && client) {
              clientData = client;
            }
          }
        } catch (clientFetchError) {
          // If client fetch fails, we still want to update client_id
          // but clientData will remain null
          console.warn("Failed to fetch client data:", clientFetchError);
        }
      }

      return { gcal_id, client_id, clientData, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getConnections = createAsyncThunk(
  "data/getConnections",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/connections`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeConnection = createAsyncThunk(
  "data/removeConnection",
  async (connection_id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/connections/${connection_id.trim()}`
      );
      return { connection_id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateConnection = createAsyncThunk(
  "data/updateConnection",
  async ({ connection_id, calendar_id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/connections/${connection_id.trim()}`,
        {
          calendar_id,
        }
      );
      return { connection_d, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createConnection = createAsyncThunk(
  "data/createConnection",
  async (arg, { rejectWithValue }) => {
    try {
      const { continueUrl, provider = "google" } =
        typeof arg === "string" ? { continueUrl: arg } : arg;
      const response = await axiosInstance.post(`/connections`, {
        continueUrl,
        provider,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const sendConnectionLink = createAsyncThunk(
  "data/sendConnectionLink",
  async ({ email, provider, continueUrl }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/connections/send-link`, {
        email,
        provider,
        continueUrl,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteWatcher = createAsyncThunk(
  "data/deleteWatcher",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/connections/${data.connection_id}/watch/${data.calendar_id}`
      );
      return { connection_id: data.connection_id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const refreshWatcher = createAsyncThunk(
  "data/refreshWatcher",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/connections/${data.connection_id}/watch/${data.calendar_id}/refresh`,
        {
          connection_id: data.connection_id,
          calendar_id: data.calendar_id,
        }
      );
      return { connection_id: data.connection_id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/** Preview backfill counts without enqueueing jobs. POST /calendars/:gcal_id/backfill/preview */
export const previewCalendarClientBackfill = createAsyncThunk(
  "data/previewCalendarClientBackfill",
  async (
    { gcal_id, client_id, windowBackDays, windowForwardDays },
    { rejectWithValue }
  ) => {
    try {
      const body = { client_id };
      if (typeof windowBackDays === "number" && Number.isFinite(windowBackDays)) {
        body.windowBackDays = windowBackDays;
      }
      if (typeof windowForwardDays === "number" && Number.isFinite(windowForwardDays)) {
        body.windowForwardDays = windowForwardDays;
      }
      const response = await axiosInstance.post(
        `/calendars/${encodeURIComponent(gcal_id)}/backfill/preview`,
        body
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Request failed"
      );
    }
  }
);

/** Queues calendar client backfill (historical scan). POST /calendars/:gcal_id/backfill */
export const queueCalendarClientBackfill = createAsyncThunk(
  "data/queueCalendarClientBackfill",
  async (
    { gcal_id, client_id, windowBackDays, windowForwardDays },
    { rejectWithValue }
  ) => {
    try {
      const body = { client_id };
      if (typeof windowBackDays === "number" && Number.isFinite(windowBackDays)) {
        body.windowBackDays = windowBackDays;
      }
      if (typeof windowForwardDays === "number" && Number.isFinite(windowForwardDays)) {
        body.windowForwardDays = windowForwardDays;
      }
      const response = await axiosInstance.post(
        `/calendars/${encodeURIComponent(gcal_id)}/backfill`,
        body
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Request failed"
      );
    }
  }
);

// ------------------------------------------------------------------------
// Slices

export const ConnectionSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    updateConnectionState: (state, action) => {
      const { connection_id, connection } = action.payload;
      state.connections = state.connections.map((conn) =>
        conn.connection_id === connection_id ? connection : conn
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // ------------------------------------------------------------
      .addCase(getConnections.pending, (state) => {
        state.loading = true;
      })
      .addCase(getConnections.fulfilled, (state, action) => {
        state.connections = action.payload.data;
        state.loading = false;
        state.connectionsLoaded = true;
      })
      .addCase(getConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(removeConnection.fulfilled, (state, action) => {
        state.connections = state.connections.filter(
          (connection) =>
            connection.connection_id !== action.payload.connection_id
        );
      })
      .addCase(updateConnection.fulfilled, (state, action) => {
        state.connections = state.connections.map((connection) =>
          connection.connection_id === action.payload.connection_id
            ? action.payload.data
            : connection
        );
      })
      // ------------------------------------------------------------
      .addCase(createConnection.fulfilled, (state, action) => {
        console.log('createConnection', action.payload);
        return;
      })
      // ------------------------------------------------------------
      .addCase(deleteWatcher.fulfilled, (state, action) => {
        const { connection_id, data } = action.payload;
        console.log("deleteWatcher", data);

        if (data) {
          // Update the connection in state if data is returned
          state.connections = state.connections.map((connection) =>
            connection.connection_id === connection_id ? data : connection
          );
        }
      })
      .addCase(refreshWatcher.fulfilled, (state, action) => {
        const { connection_id, data } = action.payload;
        if (data) {
          console.log("refreshWatcher", data);
          // Update the connection in state if data is returned
          state.connections = state.connections.map((connection) =>
            connection.connection_id === connection_id ? data : connection
          );
        }
      })
      // ------------------------------------------------------------
      .addCase(updateCalendarClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCalendarClient.fulfilled, (state, action) => {
        const { gcal_id, client_id, clientData } = action.payload;
        // Update the calendar's client_id and clientData in the connections state
        state.connections = state.connections.map((connection) => ({
          ...connection,
          children: connection.children?.map((child) =>
            child.gcal_id === gcal_id
              ? { ...child, client_id: client_id, clientData: clientData }
              : child
          ),
        }));
        state.loading = false;
      })
      .addCase(updateCalendarClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export actions and reducers
export const { setLoading, setError, updateConnectionState } =
  ConnectionSlice.actions;

// ------------------------------------------------------------------------
// Configure store

export default ConnectionSlice.reducer;
